
import React from "react";
import { cn } from "@/lib/utils";
import { motion, HTMLMotionProps } from "framer-motion";

interface GlassCardProps extends Omit<HTMLMotionProps<"div">, "className" | "children" | "variant" | "shade"> {
  hover?: boolean;
  variant?: "default" | "subtle" | "outline";
  shade?: "light" | "medium" | "dark";
  children: React.ReactNode;
  className?: string;
}

const GlassCard = ({
  className,
  hover = false,
  variant = "default",
  shade = "light",
  children,
  ...props
}: GlassCardProps) => {
  // Different background opacities based on shade
  const bgOpacityClasses = {
    light: "bg-white/20 dark:bg-gray-900/20",
    medium: "bg-white/30 dark:bg-gray-900/30",
    dark: "bg-white/40 dark:bg-gray-900/40"
  };

  // Different border styles based on variant
  const borderClasses = {
    default: "border border-white/30 dark:border-gray-800/50",
    subtle: "border border-white/10 dark:border-gray-800/30",
    outline: "border-2 border-white/40 dark:border-gray-700/60"
  };

  const hoverProps = hover ? { 
    whileHover: { 
      y: -2, 
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" 
    }
  } : {};

  return (
    <motion.div
      className={cn(
        "relative rounded-xl backdrop-blur-lg shadow-lg overflow-hidden",
        bgOpacityClasses[shade],
        borderClasses[variant],
        hover && "transition-all hover:shadow-xl hover:translate-y-[-2px]",
        className
      )}
      {...hoverProps}
      {...props}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 dark:from-white/5 dark:to-transparent pointer-events-none" />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
};

export default GlassCard;
